import { useEffect, useState, useCallback } from "react";
import {
  Eye, EyeOff, ShieldAlert, ShieldX, Plus, Loader2, ArrowLeft, Download,
  FileSpreadsheet, RefreshCw, Ban, Info, Mail, Users,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";

/**
 * Lista de Vigilancia FELVORA (Msg 583 · feb-2026)
 * -------------------------------------------------
 * Lista PROPIA de la plataforma, separada de las 7 oficiales (OFAC, ONU, SAT 69/69-B, LPB,
 * PEP). Sólo el super_admin da de alta/baja. Crítica pero NO bloqueante — cada match genera
 * ALERTA ALTA al OC (in-app) + correo Brevo GENÉRICO (sin PII). El super_admin ve SOLO el
 * agregado (entidades_reescaneadas, coincidencias_nuevas). Cero exposición de nombres.
 */
export default function ListaVigilancia() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resumen, setResumen] = useState(null);
  const [entradas, setEntradas] = useState([]);
  const [reescaneos, setReescaneos] = useState([]);
  const [showInactivos, setShowInactivos] = useState(false);

  // ---- alta manual ----
  const emptyForm = {
    nombre: "", rfc: "", curp: "", identificacion: "", alias: "",
    fecha_nacimiento: "", nacionalidad: "",
    fuente_origen: "", fuente_libre: "", referencia_fuente: "",
    categoria_riesgo: "",
    motivo_alta: "", observaciones: "",
  };
  const [openAlta, setOpenAlta] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fuentesCatalogo, setFuentesCatalogo] = useState([]);
  const [categoriasCatalogo, setCategoriasCatalogo] = useState([]);
  const [nivelPorCategoria, setNivelPorCategoria] = useState({});
  const [categoriaPepValue, setCategoriaPepValue] = useState("");

  // ---- confirmación agregada (Decisión 3) ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  // ---- baja ----
  const [openBaja, setOpenBaja] = useState(null);
  const [motivoBaja, setMotivoBaja] = useState("");
  const [bajaBusy, setBajaBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, e, t, f, c] = await Promise.all([
        api.get("/vigilancia/resumen"),
        api.get("/vigilancia/entradas"),
        api.get("/vigilancia/reescaneos"),
        api.get("/vigilancia/fuentes"),
        api.get("/vigilancia/categorias"),
      ]);
      setResumen(r.data);
      setEntradas(e.data);
      setReescaneos(t.data);
      setFuentesCatalogo(f.data?.fuentes || []);
      setCategoriasCatalogo(c.data?.categorias || []);
      setNivelPorCategoria(c.data?.nivel_por_categoria || {});
      setCategoriaPepValue(c.data?.categoria_pep || "");
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "super_admin") {
      setError(true);
      setLoading(false);
      return;
    }
    if (user) load();
  }, [user, load]);

  // ---- polling ligero al ejecutar re-escaneo: 2s x 6 tries ----
  const esperarRescan = async (entradaId) => {
    for (let i = 0; i < 6; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await api.get(`/vigilancia/entradas/${entradaId}`);
        if (data.ultimo_rescan_estatus === "completado") {
          return data;
        }
      } catch (_e) { /* ignore */ }
    }
    return null;
  };

  const submitAlta = async () => {
    if (!form.nombre.trim()) {
      toast.error("El nombre o razón social es obligatorio");
      return;
    }
    if (!form.fuente_origen) {
      toast.error("Selecciona la fuente u origen del registro");
      return;
    }
    if (form.fuente_origen === "Otra" && !form.fuente_libre.trim()) {
      toast.error("Escribe la fuente cuando eliges 'Otra'");
      return;
    }
    if (!form.motivo_alta.trim() || form.motivo_alta.trim().length < 10) {
      toast.error("El fundamento debe tener al menos 10 caracteres");
      return;
    }
    if (!form.categoria_riesgo) {
      toast.error("Selecciona la categoría de riesgo");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/vigilancia/entradas", {
        ...form,
        alias: form.alias
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean),
      });
      toast.success("Registro agregado. Re-escaneo focalizado corriendo en segundo plano.");
      setOpenAlta(false);
      setForm(emptyForm);
      await load();
      // Espera a que termine el rescan para mostrar el agregado.
      const completed = await esperarRescan(data.entrada.id);
      if (completed) {
        setConfirmData({
          nombre_masked: (completed.nombre || "").slice(0, 3) + "***",
          entidades: completed.ultimo_rescan_entidades || 0,
          coincidencias: completed.ultimo_rescan_coincidencias || 0,
          alertas: completed.ultimo_rescan_alertas || 0,
          correos: completed.ultimo_rescan_correos || 0,
        });
        setConfirmOpen(true);
        await load();
      } else {
        toast.info("El re-escaneo sigue corriendo. Revisa la traza en unos momentos.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo dar de alta el registro");
    } finally {
      setSaving(false);
    }
  };

  const doBaja = async () => {
    if (motivoBaja.trim().length < 10) {
      toast.error("El motivo de baja debe tener al menos 10 caracteres");
      return;
    }
    setBajaBusy(true);
    try {
      await api.post(`/vigilancia/entradas/${openBaja.id}/baja`, { motivo_baja: motivoBaja });
      toast.success("Registro dado de baja. Excluido de futuros re-escaneos.");
      setOpenBaja(null);
      setMotivoBaja("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo dar de baja");
    } finally {
      setBajaBusy(false);
    }
  };

  const descargarPlantilla = async () => {
    try {
      const resp = await api.get("/vigilancia/plantilla", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla_lista_vigilancia_felvora_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("No se pudo descargar la plantilla");
    }
  };

  if (error)
    return (
      <div>
        <PageHeader title="Lista de Vigilancia FELVORA" subtitle="Panel de plataforma" />
        <div
          className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400"
          data-testid="vigilancia-acceso-denegado"
        >
          <ShieldX className="h-10 w-10" />
          <p className="text-sm">
            Acceso exclusivo del Super Administrador de la plataforma.
          </p>
        </div>
      </div>
    );

  const entradasVisibles = showInactivos ? entradas : entradas.filter((e) => e.activo);

  return (
    <div data-testid="lista-vigilancia">
      <PageHeader
        title="Lista de Vigilancia FELVORA"
        subtitle="Lista propia de la plataforma · separada del catálogo oficial · sólo alerta, NO bloquea"
      >
        <Button
          data-testid="btn-volver-listas"
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin-listas")}
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Volver a Centro de Listas
        </Button>
        <Button
          data-testid="btn-descargar-plantilla"
          variant="outline"
          size="sm"
          onClick={descargarPlantilla}
        >
          <Download className="mr-1.5 h-4 w-4" /> Plantilla Excel
        </Button>
        <Button
          data-testid="btn-nueva-entrada"
          size="sm"
          className="bg-primary hover:bg-sidebar-hover"
          onClick={() => setOpenAlta(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Agregar registro
        </Button>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-6 p-6 md:p-8">
          {/* Disclaimer del candado de privacidad */}
          <div
            className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs text-indigo-900"
            data-testid="vigilancia-disclaimer"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
            <div>
              <p className="font-semibold uppercase tracking-wide">Candado de privacidad activo</p>
              <p className="mt-0.5 leading-relaxed">
                Esta lista es propia de la plataforma. Al agregar un registro, el sistema
                cotejará la cartera de todas las entidades EN SEGUNDO PLANO, generará alerta
                ALTA en la bandeja del Oficial de Cumplimiento correspondiente y le enviará un
                correo genérico (sin datos personales). Verás SÓLO contadores agregados: nunca
                nombres de entidad ni de cliente. Un match NO bloquea automáticamente — lo
                decide el Oficial por el flujo estándar.
              </p>
            </div>
          </div>

          {/* KPIs */}
          {resumen && (
            <div
              className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
              data-testid="vigilancia-kpis"
            >
              <StatCard label="Registros activos" value={resumen.activos} icon="ShieldAlert" accent="primary" />
              <StatCard label="Registros dados de baja" value={resumen.inactivos} icon="Ban" accent="slate" />
              <StatCard label="Coincidencias detectadas" value={resumen.total_coincidencias} icon="Search" accent="amber" />
              <StatCard label="Alertas enviadas al OC" value={resumen.total_alertas} icon="AlertTriangle" accent="red" />
              <StatCard label="Correos genéricos enviados" value={resumen.total_correos} icon="Mail" accent="green" />
            </div>
          )}

          {/* Registros */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700">
                <ShieldAlert className="h-4 w-4 text-indigo-600" /> Registros en la lista
              </h3>
              <Button
                data-testid="btn-toggle-inactivos"
                variant="outline"
                size="sm"
                onClick={() => setShowInactivos((v) => !v)}
              >
                {showInactivos ? (
                  <>
                    <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Ocultar dados de baja
                  </>
                ) : (
                  <>
                    <Eye className="mr-1.5 h-3.5 w-3.5" /> Ver dados de baja
                  </>
                )}
              </Button>
            </div>
            <DataTable
              testid="table-vigilancia-entradas"
              rows={entradasVisibles}
              columns={[
                {
                  key: "nombre",
                  label: "Nombre / Razón social",
                  render: (r) => (
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{r.nombre}</p>
                      {r.alias?.length > 0 && (
                        <p className="truncate text-xs text-slate-400">
                          Alias: {r.alias.slice(0, 2).join(", ")}
                          {r.alias.length > 2 ? ` +${r.alias.length - 2}` : ""}
                        </p>
                      )}
                    </div>
                  ),
                },
                {
                  key: "rfc_curp",
                  label: "RFC / CURP",
                  render: (r) => (
                    <div className="text-xs font-mono text-slate-600">
                      <div>{r.rfc || "—"}</div>
                      <div className="text-slate-400">{r.curp || "—"}</div>
                    </div>
                  ),
                },
                { key: "fecha_alta", label: "Alta", kind: "date" },
                {
                  key: "categoria_riesgo",
                  label: "Categoría de riesgo",
                  render: (r) => {
                    const cat = r.categoria_riesgo || "categoría no especificada";
                    const isNS = cat === "categoría no especificada";
                    const isPep = r.es_pep === true;
                    let cls = "border-red-200 bg-red-50 text-red-700";
                    if (isNS) cls = "border-slate-200 bg-slate-50 text-slate-500 italic";
                    else if (isPep) cls = "border-amber-300 bg-amber-50 text-amber-800";
                    return (
                      <span
                        data-testid={`fila-categoria-${r.id}`}
                        className={`inline-block max-w-[220px] truncate rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
                        title={cat}
                      >
                        {cat}
                      </span>
                    );
                  },
                },
                {
                  key: "fuente_origen",
                  label: "Fuente / Origen",
                  render: (r) => {
                    const fu = r.fuente_origen || "origen no especificado";
                    const isNS = fu === "origen no especificado";
                    const libre = fu === "Otra" && r.fuente_libre ? r.fuente_libre : null;
                    return (
                      <div className="min-w-0" data-testid={`fila-fuente-${r.id}`}>
                        <p
                          className={`truncate text-xs font-medium ${
                            isNS ? "text-slate-400 italic" : "text-indigo-700"
                          }`}
                        >
                          {libre || fu}
                        </p>
                        {r.referencia_fuente && (
                          <p className="truncate text-[11px] text-slate-500">
                            {r.referencia_fuente}
                          </p>
                        )}
                      </div>
                    );
                  },
                },
                { key: "agregado_por", label: "Agregado por" },
                {
                  key: "ultimo_rescan_coincidencias",
                  label: "Coincidencias",
                  render: (r) => (
                    <span
                      className={`font-semibold tabular-nums ${
                        (r.ultimo_rescan_coincidencias || 0) > 0
                          ? "text-amber-600"
                          : "text-slate-500"
                      }`}
                    >
                      {r.ultimo_rescan_coincidencias ?? 0}
                    </span>
                  ),
                },
                {
                  key: "activo",
                  label: "Estatus",
                  render: (r) => (
                    <StatusBadge value={r.activo ? "Activa" : "Dada de baja"} />
                  ),
                },
                {
                  key: "acciones",
                  label: "Acciones",
                  render: (r) =>
                    r.activo ? (
                      <Button
                        data-testid={`btn-baja-${r.id}`}
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenBaja(r);
                        }}
                      >
                        <Ban className="mr-1 h-3.5 w-3.5" /> Dar de baja
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">Inactiva</span>
                    ),
                },
              ]}
              emptyLabel="Sin registros en la lista. Agrega uno con el botón superior."
            />
          </div>

          {/* Traza agregada (Decisión 3 — sin PII) */}
          <div>
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700">
              <RefreshCw className="h-4 w-4 text-forest-600" /> Traza de re-escaneo (agregada,
              sin datos personales)
            </h3>
            <DataTable
              testid="table-vigilancia-reescaneos"
              rows={reescaneos}
              columns={[
                { key: "fecha", label: "Fecha", kind: "date" },
                {
                  key: "entrada_nombre_masked",
                  label: "Registro (enmascarado)",
                  render: (r) => (
                    <span className="font-mono text-xs text-slate-500">
                      {r.entrada_nombre_masked}
                    </span>
                  ),
                },
                {
                  key: "entidades_reescaneadas",
                  label: "Entidades re-escaneadas",
                  render: (r) => (
                    <span className="tabular-nums font-medium">
                      {r.entidades_reescaneadas ?? 0}
                    </span>
                  ),
                },
                {
                  key: "coincidencias_nuevas",
                  label: "Coincidencias nuevas",
                  render: (r) => (
                    <span
                      className={`font-semibold tabular-nums ${
                        (r.coincidencias_nuevas || 0) > 0
                          ? "text-amber-600"
                          : "text-slate-500"
                      }`}
                    >
                      {r.coincidencias_nuevas ?? 0}
                    </span>
                  ),
                },
                {
                  key: "alertas_generadas",
                  label: "Alertas al OC",
                  render: (r) => (
                    <span className="tabular-nums font-medium text-red-600">
                      {r.alertas_generadas ?? 0}
                    </span>
                  ),
                },
                {
                  key: "correos_enviados",
                  label: "Correos enviados",
                  render: (r) => (
                    <span className="tabular-nums font-medium text-emerald-600">
                      {r.correos_enviados ?? 0}
                    </span>
                  ),
                },
                { key: "usuario", label: "Ejecutó" },
              ]}
              emptyLabel="Aún no se han ejecutado re-escaneos"
            />
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
              <Info className="h-3.5 w-3.5" /> El nombre completo del registro y las entidades
              afectadas quedan reservados a las bandejas de cumplimiento de cada entidad.
              Aquí sólo se muestran contadores.
            </p>
          </div>
        </div>
      )}

      {/* ================= Alta manual ================= */}
      <Dialog open={openAlta} onOpenChange={setOpenAlta}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
          data-testid="dialog-alta-vigilancia"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-indigo-600" /> Agregar registro a la Lista
              de Vigilancia FELVORA
            </DialogTitle>
            <DialogDescription>
              Alta manual, uno por uno. Al confirmar, el sistema re-escanea EN SEGUNDO PLANO
              contra sólo este registro y notifica al Oficial de Cumplimiento de las entidades
              donde haya coincidencia. Tú sólo verás el contador agregado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">
                Nombre o razón social <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="alta-nombre"
                value={form.nombre}
                onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                placeholder="Juan Pérez López / Comercializadora del Bajío, S.A. de C.V."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-xs">RFC (opcional)</Label>
                <Input
                  data-testid="alta-rfc"
                  value={form.rfc}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, rfc: e.target.value.toUpperCase() }))
                  }
                  placeholder="PELJ800101ABC"
                />
              </div>
              <div>
                <Label className="mb-1 block text-xs">CURP (opcional)</Label>
                <Input
                  data-testid="alta-curp"
                  value={form.curp}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, curp: e.target.value.toUpperCase() }))
                  }
                  placeholder="PELJ800101HDFRPN01"
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Alias (separados por coma)</Label>
              <Input
                data-testid="alta-alias"
                value={form.alias}
                onChange={(e) => setForm((p) => ({ ...p, alias: e.target.value }))}
                placeholder="Juan Perez, J. Perez L., El Juanito"
              />
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
                Fuente u origen del registro
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="mb-1 block text-xs">
                    Fuente <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={form.fuente_origen}
                    onValueChange={(v) => setForm((p) => ({ ...p, fuente_origen: v }))}
                  >
                    <SelectTrigger data-testid="alta-fuente-origen">
                      <SelectValue placeholder="Selecciona la fuente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fuentesCatalogo.map((f) => (
                        <SelectItem key={f} value={f} data-testid={`alta-fuente-opt-${f}`}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">
                    Referencia exacta (opcional)
                  </Label>
                  <Input
                    data-testid="alta-referencia"
                    value={form.referencia_fuente}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, referencia_fuente: e.target.value }))
                    }
                    placeholder="Ej: publicación UIF del 15/07/2026 · Boletín #DOF-2026-0142"
                  />
                </div>
              </div>
              {form.fuente_origen === "Otra" && (
                <div>
                  <Label className="mb-1 block text-xs">
                    Describe la fuente <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="alta-fuente-libre"
                    value={form.fuente_libre}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, fuente_libre: e.target.value }))
                    }
                    placeholder="De dónde proviene la información..."
                  />
                </div>
              )}
              <p className="text-[11px] text-indigo-700">
                El Oficial de Cumplimiento verá esta fuente en la alerta y en el detalle de la
                coincidencia para poder verificar el registro original.
              </p>
            </div>
            {/* ------------- Categoría de riesgo ------------- */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Categoría de riesgo
              </p>
              <div>
                <Label className="mb-1 block text-xs">
                  Categoría <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.categoria_riesgo}
                  onValueChange={(v) => setForm((p) => ({ ...p, categoria_riesgo: v }))}
                >
                  <SelectTrigger data-testid="alta-categoria-riesgo">
                    <SelectValue placeholder="Selecciona la categoría..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriasCatalogo.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`alta-categoria-opt-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.categoria_riesgo && (
                <div
                  className={`rounded-md border px-3 py-2 text-xs ${
                    form.categoria_riesgo === categoriaPepValue
                      ? "border-amber-300 bg-amber-100/70 text-amber-900"
                      : "border-red-300 bg-red-50 text-red-900"
                  }`}
                  data-testid="alta-preview-nivel"
                >
                  <p className="font-semibold">
                    Nivel de alerta que verá el Oficial:{" "}
                    <span className="uppercase">
                      {nivelPorCategoria[form.categoria_riesgo] || "Alto"}
                    </span>
                  </p>
                  {form.categoria_riesgo === categoriaPepValue ? (
                    <p className="mt-1 leading-relaxed">
                      Un PEP no es un señalamiento negativo. La alerta será de nivel{" "}
                      <b>Medio</b>, marcará al cliente para <b>debida diligencia reforzada</b>{" "}
                      y le pedirá al Oficial documentar el origen de los recursos.
                    </p>
                  ) : (
                    <p className="mt-1 leading-relaxed">
                      Alerta de nivel <b>Alto</b> — el Oficial de Cumplimiento la revisará y
                      decidirá si confirma o descarta como falso positivo. Nunca se bloquea al
                      cliente automáticamente.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-xs">Fecha de nacimiento</Label>
                <Input
                  data-testid="alta-fnac"
                  type="date"
                  value={form.fecha_nacimiento}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, fecha_nacimiento: e.target.value }))
                  }
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Si coincide con la del cliente, suma 5 pts al score (desambigua homónimos).
                </p>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Nacionalidad</Label>
                <Input
                  data-testid="alta-nac"
                  value={form.nacionalidad}
                  onChange={(e) => setForm((p) => ({ ...p, nacionalidad: e.target.value }))}
                  placeholder="Mexicana"
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">
                Fundamento del alta <span className="text-red-500">*</span>
              </Label>
              <Textarea
                data-testid="alta-motivo"
                rows={4}
                value={form.motivo_alta}
                onChange={(e) => setForm((p) => ({ ...p, motivo_alta: e.target.value }))}
                placeholder="Explica por qué se agrega esta persona a la Lista de Vigilancia (mínimo 10 caracteres). Ej: 'Reporte interno FELVORA #INT-2026-0142: operación en efectivo estructurada por debajo del umbral detectada en 3 entidades.'"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Queda en bitácora junto con tu nombre y fecha. Requerido para revisiones
                internas y ante CNBV/UIF.
              </p>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Observaciones (opcional)</Label>
              <Textarea
                data-testid="alta-obs"
                rows={2}
                value={form.observaciones}
                onChange={(e) => setForm((p) => ({ ...p, observaciones: e.target.value }))}
                placeholder="Notas internas, plazo de revisión, referencias..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAlta(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="alta-confirmar"
              onClick={submitAlta}
              disabled={saving}
              className="bg-primary hover:bg-sidebar-hover"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Guardando y re-escaneando...
                </>
              ) : (
                <>
                  <Plus className="mr-1.5 h-4 w-4" /> Agregar y re-escanear
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================= Confirmación agregada (Decisión 3) ================= */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="dialog-confirmacion-agregada"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-emerald-700">
              <RefreshCw className="h-5 w-5" /> Re-escaneo completado
            </DialogTitle>
            <DialogDescription>
              El re-escaneo focalizado terminó. Estos son los contadores agregados que
              respetan el candado de privacidad.
            </DialogDescription>
          </DialogHeader>
          {confirmData && (
            <div
              className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm"
              data-testid="confirmacion-contenido"
            >
              <div className="flex items-center gap-2 text-emerald-800">
                <Users className="h-4 w-4" />
                <span>
                  Se re-escanearon{" "}
                  <b data-testid="cnt-entidades">{confirmData.entidades}</b> entidad(es).
                </span>
              </div>
              <div className="flex items-center gap-2 text-emerald-800">
                <ShieldAlert className="h-4 w-4" />
                <span>
                  <b data-testid="cnt-coincidencias">{confirmData.coincidencias}</b>{" "}
                  coincidencia(s) detectada(s).
                </span>
              </div>
              <div className="flex items-center gap-2 text-emerald-800">
                <ShieldAlert className="h-4 w-4 text-red-600" />
                <span>
                  <b data-testid="cnt-alertas">{confirmData.alertas}</b> alerta(s) crítica(s)
                  colgada(s) en las bandejas de los Oficiales de Cumplimiento.
                </span>
              </div>
              <div className="flex items-center gap-2 text-emerald-800">
                <Mail className="h-4 w-4" />
                <span>
                  <b data-testid="cnt-correos">{confirmData.correos}</b> correo(s) genérico(s)
                  enviado(s) al OC (sin datos personales).
                </span>
              </div>
              <div className="mt-2 rounded-md border border-indigo-200 bg-white/70 p-3 text-xs text-indigo-800">
                <p className="font-semibold">Candado de privacidad respetado.</p>
                <p className="mt-0.5">
                  No se muestran ni los nombres de las entidades ni los nombres de los
                  clientes coincidentes. Registro dado de alta:{" "}
                  <span className="font-mono">{confirmData.nombre_masked}</span>.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="confirmacion-ok"
              onClick={() => setConfirmOpen(false)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================= Baja auditable ================= */}
      <Dialog open={!!openBaja} onOpenChange={(v) => !v && setOpenBaja(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-baja-vigilancia">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-slate-800">
              <Ban className="h-5 w-5 text-slate-600" /> Dar de baja el registro
            </DialogTitle>
            <DialogDescription>
              La baja marca el registro como <b>inactivo</b> — nunca se borra. Los matches y
              alertas previamente generados <b>NO se cierran</b> (son evidencia). Sólo se
              excluye de futuros re-escaneos.
            </DialogDescription>
          </DialogHeader>
          {openBaja && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-400">Registro a dar de baja</p>
                <p className="font-medium">{openBaja.nombre}</p>
                {openBaja.rfc && (
                  <p className="text-xs text-slate-500 font-mono">RFC: {openBaja.rfc}</p>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-xs">
                  Motivo de baja <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  data-testid="baja-motivo"
                  rows={4}
                  value={motivoBaja}
                  onChange={(e) => setMotivoBaja(e.target.value)}
                  placeholder="Explica por qué se da de baja (mínimo 10 caracteres). Ej: 'Se aclaró vía carta oficial del interesado; homónimo confirmado.'"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBaja(null)}>
              Cancelar
            </Button>
            <Button
              data-testid="baja-confirmar"
              onClick={doBaja}
              disabled={bajaBusy}
              className="bg-slate-800 hover:bg-slate-900 text-white"
            >
              {bajaBusy ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Dando de baja...
                </>
              ) : (
                <>
                  <Ban className="mr-1.5 h-4 w-4" /> Confirmar baja
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
